import Container, { Service } from 'typedi';
import {
    EventFormatterBuilder,
    EventFormatterBuilderFactory,
    Level,
} from '@unified-client/event-formatter';
import { ILogger, LoggerProvider } from '../logger';

import { ClickStreamTrackingProvider } from '../tracking';
import IServiceWorkerClickStreamEventData from './models/IServiceWorkerClickStreamEventData';
import { IServiceWorkerParams } from './models/IServiceWorkerParams';
import { IUrbanAirshipOptions } from './models/IUrbanAirshipOptions';
import { WebPushElasticEvent } from './enums/consts';
import { WindowToken } from '../../injection-tokens';
import { v4 as uuidv4 } from 'uuid';
import PageContextManager from 'page-context-manager';

@Service()
export class ServiceWorkerService {
    private readonly _logger: ILogger;
    private readonly _window: Window;
    private _eventFormatterBuilder: EventFormatterBuilder;
    private _clickStreamTrackingProvider: ClickStreamTrackingProvider;

    private readonly _correlationID: string;
    private readonly _serviceWorkerFilename: string = 'push-worker.js';

    constructor() {
        this._logger = Container.get(LoggerProvider).getLogger('ServiceWorkerService');
        this._clickStreamTrackingProvider = Container.get(ClickStreamTrackingProvider);
        this._window = Container.get(WindowToken);
        const eventFormatterBuilderFactory = Container.get(EventFormatterBuilderFactory);

        this._eventFormatterBuilder =
            eventFormatterBuilderFactory.createEventFormatterBuilder('ServiceWorkerService');
        this._correlationID = uuidv4();
    }

    public getServiceWorkerUrl = () => {
        let baseUrl = `/${this._serviceWorkerFilename}`;

        const { versionNumber } = PageContextManager.getReleaseData();
        const appVersionQueryString = `v=${versionNumber}`;

        const serviceWorkerParams = this._getServiceWorkerParams();
        const paramsQueryString = `params=${encodeURIComponent(
            JSON.stringify(serviceWorkerParams),
        )}`;

        return `${baseUrl}?${appVersionQueryString}&${paramsQueryString}`;
    };

    public registerServiceWorker = () => {
        try {
            if ('serviceWorker' in this._window.navigator) {
                const serviceWorkerUrl = this.getServiceWorkerUrl();

                const formatter =
                    this._eventFormatterBuilder.createFormatter('registerServiceWorker');
                const eventData: IServiceWorkerClickStreamEventData = {
                    event: WebPushElasticEvent.RegisterServiceWorker,
                    url: serviceWorkerUrl,
                    isOk: true,
                };

                this._window.navigator.serviceWorker
                    .register(serviceWorkerUrl)
                    .then((swRegistration) => {
                        this._sendElasticEvent(formatter, eventData);
                        this._logger.log('Service worker is registered');
                    })
                    .catch((error) => {
                        eventData.isOk = false;
                        eventData.errorDescription = `Service worker registration failed: ${
                            error?.stack || error
                        }`;

                        this._sendElasticEvent(formatter, eventData, Level.error);
                        this._logger.error(
                            'Service worker registration failed',
                            serviceWorkerUrl,
                            error,
                        );
                    });
            } else this._logger.log('Service workers are not supported');
        } catch (err) {}
    };

    private _getServiceWorkerParams = (): IServiceWorkerParams => {
        const serviceWorkerParams: IServiceWorkerParams = {
            urbanAirshipOptions: this._getUrbanAirshipOptions(),
        };

        return serviceWorkerParams;
    };

    private _getUrbanAirshipOptions = (): IUrbanAirshipOptions => {
        const { appKey, token, vapidPublicKey, defaultTitle, defaultActionUrl, defaultIconUrl } =
            PageContextManager.getAirshipData() || {};

        const urbanAirshipOptions: IUrbanAirshipOptions = {
            isEnabled: true,
            appKey: appKey,
            token: token,
            vapidPublicKey: vapidPublicKey,
            defaultTitle: defaultTitle,
            defaultIcon: defaultIconUrl,
            defaultActionURL: defaultActionUrl,
        };

        return urbanAirshipOptions;
    };

    private _sendElasticEvent = (
        formatter: any,
        eventData: IServiceWorkerClickStreamEventData,
        level: Level = Level.information,
    ) => {
        const event = formatter.formatUCEvent(
            { message: eventData.event },
            { correlationID: this._correlationID, ...(level && { level }) },
            { ...eventData },
        );
        this._clickStreamTrackingProvider.sendEventV2(event);
    };
}
